import { map, size, get, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import { POSITIONS, TYPES } from 'constants/topNavigation';

import Breadcrumb from './Breadcrumb';
import FeaturedCollections from './FeaturedCollections';
import Item from './Item';
import Link from './Link';

const TopNavigationSubNavigationMobile = class extends React.PureComponent {
  static hasChildren = node => !!size(get(node, 'subNavigations'));

  static isLink = node =>
    !TopNavigationSubNavigationMobile.hasChildren(node) && !!get(node, 'linkUrl');

  static isBold = node =>
    get(node, 'position') === POSITIONS.PRIMARY || get(node, 'position') === POSITIONS.SECONDARY;

  static hasBreadcrumb = parentNode => !!parentNode;

  static isSalesColor = node => node?.isSalesColorRed || false;

  static hasMainLink = node => !!get(node, 'mainLinkLabel') && !!get(node, 'mainLinkUrl');

  render() {
    const {
      level,
      node,
      parentNode,
      setCurrentTopNavigationNode,
      trackNavigation,
      trackNavigationClick,
      trackNavigationRerunRenamingExperimentEngagement,
      navigationRerunRenamingExperimentAnalyticsData,
    } = this.props;

    const handleNavigationTracking = subNavigationLabel => {
      const parentNodeLabel = get(parentNode, 'label');
      const nodeLabel = get(node, 'label');

      switch (level) {
        case TYPES.LEVEL_3:
          trackNavigationClick({
            navigationType: TYPES.LEVEL_3,
            navigationLevel1: parentNodeLabel,
            navigationLevel2: nodeLabel,
            navigationLevel3: subNavigationLabel,
            navigationSource: window.location.href,
          });
          break;
        case TYPES.LEVEL_2:
          trackNavigationClick({
            navigationType: TYPES.LEVEL_2,
            navigationLevel1: nodeLabel,
            navigationLevel2: subNavigationLabel,
            navigationSource: window.location.href,
          });
          break;
        default:
          trackNavigationClick({
            navigationType: TYPES.GLOBAL,
            navigationLevel1: subNavigationLabel,
            navigationSource: window.location.href,
          });

          if (
            navigationRerunRenamingExperimentAnalyticsData &&
            Object.keys(navigationRerunRenamingExperimentAnalyticsData).length
          ) {
            trackNavigationRerunRenamingExperimentEngagement(
              navigationRerunRenamingExperimentAnalyticsData.dyDecisionId,
              navigationRerunRenamingExperimentAnalyticsData.dyVariationId
            );
          }
      }
    };

    const handleItemClick = subNavigation => {
      setCurrentTopNavigationNode(get(subNavigation, 'id'));
      handleNavigationTracking(get(subNavigation, 'label'));
    };

    const handleLinkClick = ({ url, subNavigationLabel }) => {
      trackNavigation(url);
      handleNavigationTracking(subNavigationLabel);
    };

    const handleBreadCrumbClick = () => {
      const navigationLevel1 = get(parentNode, 'label')
        ? get(parentNode, 'label')
        : get(node, 'label');
      const navigationLevel2 = get(parentNode, 'label') ? get(node, 'label') : null;
      trackNavigationClick({
        navigationType: TYPES.BACK,
        navigationLevel1,
        navigationLevel2,
        navigationSource: window.location.href,
      });
      setCurrentTopNavigationNode(get(parentNode, 'id'));
    };

    return (
      <div className="TopNavigationSubNavigationMobile">
        <ul className="TopNavigationSubNavigationMobile__list">
          {TopNavigationSubNavigationMobile.hasBreadcrumb(parentNode) && (
            <li
              className="TopNavigationSubNavigationMobile__list-item TopNavigationSubNavigationMobile__list-item--breacrumb"
              key="breadcrumb"
            >
              <Breadcrumb label={get(node, 'label')} onClick={() => handleBreadCrumbClick()} />
            </li>
          )}
          {map(get(node, 'subNavigations'), (subNavigation, index) => (
            <li
              className="TopNavigationSubNavigationMobile__list-item TopNavigationSubNavigationMobile__list-item--subnavigation"
              key={`item-${index}`}
            >
              {TopNavigationSubNavigationMobile.isLink(subNavigation) ? (
                <>
                  <Link
                    label={get(subNavigation, 'label')}
                    href={get(subNavigation, 'linkUrl')}
                    onClick={() =>
                      handleLinkClick({
                        url: get(subNavigation, 'linkUrl'),
                        subNavigationLabel: get(subNavigation, 'label'),
                      })
                    }
                    isBold={TopNavigationSubNavigationMobile.isBold(subNavigation)}
                    isSalesColor={TopNavigationSubNavigationMobile.isSalesColor(subNavigation)}
                  />
                </>
              ) : (
                <Item
                  label={get(subNavigation, 'label')}
                  onClick={() => handleItemClick(subNavigation)}
                  isSalesColor={TopNavigationSubNavigationMobile.isSalesColor(subNavigation)}
                />
              )}
            </li>
          ))}
          {!isEmpty(get(node, 'featuredCollections')) && (
            <FeaturedCollections
              featuredCollections={get(node, 'featuredCollections')}
              trackNavigation={trackNavigation}
              trackNavigationClick={trackNavigationClick}
            />
          )}
          {TopNavigationSubNavigationMobile.hasMainLink(node) && (
            <li
              className="TopNavigationSubNavigationMobile__list-item TopNavigationSubNavigationMobile__list-item--main"
              key="main"
            >
              <Link
                label={get(node, 'mainLinkLabel')}
                href={get(node, 'mainLinkUrl')}
                onClick={() =>
                  handleLinkClick({
                    url: get(node, 'mainLinkUrl'),
                    subNavigationLabel: get(node, 'mainLinkLabel'),
                  })
                }
              />
            </li>
          )}
        </ul>
        <style jsx>
          {`
            .TopNavigationSubNavigationMobile__list {
              margin: 0;
              padding: 0;
              list-style: none;
            }
          `}
        </style>
      </div>
    );
  }
};

const nodePropType = () =>
  PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    label: PropTypes.string,
    position: PropTypes.oneOf(Object.values(POSITIONS)),
    subNavigations: PropTypes.arrayOf(nodePropType).isRequired,
    mainLinkLabel: PropTypes.string,
    mainLinkUrl: PropTypes.string,
    image: PropTypes.string,
  });

TopNavigationSubNavigationMobile.propTypes = {
  node: nodePropType(),
  parentNode: nodePropType(),
  setCurrentTopNavigationNode: PropTypes.func.isRequired,
  trackNavigation: PropTypes.func.isRequired,
  trackNavigationClick: PropTypes.func.isRequired,
  trackNavigationRerunRenamingExperimentEngagement: PropTypes.func,
  navigationRerunRenamingExperimentAnalyticsData: PropTypes.shape({
    dyDecisionId: PropTypes.string,
    dyVariationId: PropTypes.number,
  }),
  level: PropTypes.string.isRequired,
};

TopNavigationSubNavigationMobile.defaultProps = {
  node: null,
  parentNode: null,
  navigationRerunRenamingExperimentAnalyticsData: null,
  trackNavigationRerunRenamingExperimentEngagement: () => {},
};

export default TopNavigationSubNavigationMobile;
